import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Zap, Shield, GitBranch, BarChart3, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const sections = [
  {
    icon: Zap,
    title: 'Getting Started',
    desc: 'Connect your CRM and have reps seeing prioritized actions within hours.',
    steps: [
      'Connect your CRM (Salesforce, Zoho, or LeadSquared) via OAuth, no engineering required.',
      'Invite your team and assign roles (CRO, Sales Manager, Rep, RevOps).',
      'Forsyt ingests your pipeline data and scores every deal automatically.',
      'Each rep sees a personalized "Today\'s Focus" card with their #1 priority and next step.',
    ],
  },
  {
    icon: BarChart3,
    title: 'Risk Radar',
    desc: 'How pipeline health scoring works.',
    steps: [
      'Forsyt analyzes deal stage, last activity date, contact engagement, and email/call frequency.',
      'Each opportunity gets a health score: Healthy, Needs Attention, At Risk, or Critical.',
      'Deals with no activity for 7+ days are auto-flagged. Stalled stage transitions trigger alerts.',
      'CROs see an aggregate pipeline health view. Managers can drill into team-level risk.',
    ],
  },
  {
    icon: Users,
    title: 'Role-Based Views',
    desc: 'Every persona sees what matters to them.',
    steps: [
      'CRO View: Pipeline health, team performance, forecast accuracy, risk radar.',
      'Sales Manager View: Rep activity, deal-level risk, coaching priorities.',
      'Rep View: Today\'s focus, AI deal chat, auto-updated CRM fields, action cards.',
      'RevOps View: Process compliance, data hygiene, stage conversion metrics.',
    ],
  },
  {
    icon: GitBranch,
    title: 'Integrations',
    desc: 'Forsyt sits on top of your existing stack.',
    steps: [
      'CRM: Salesforce, Zoho, LeadSquared, HubSpot (coming soon).',
      'Call Intelligence: Gong, Fireflies, native AI Notetaker.',
      'Email: Gmail, Outlook, automatic sync of sent/received emails per deal.',
      'Messaging: Slack, deal updates, alerts, and actions delivered in-channel.',
    ],
  },
  {
    icon: Shield,
    title: 'Security & Privacy',
    desc: 'Enterprise-grade security by design.',
    steps: [
      'Zero-data-retention architecture: your data is processed in-memory and never stored on Forsyt servers.',
      'All connections use OAuth 2.0 with minimal permission scopes.',
      'ISO/IEC 27001:2022 certified. Certificate and Statement of Applicability summary available on request under NDA.',
      'Deploy on your own infrastructure or use Forsyt\'s managed cloud.',
    ],
  },
];

export default function Documentation() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Documentation, Forsyt Revenue OS"
        description="Everything you need to get started with Forsyt. Connect your CRM, set up your team, and start seeing AI-powered action prescriptions in hours."
        path="/resources/documentation"
      />

      {/* Hero */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <BookOpen className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="docs-headline">
            Documentation
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to get started with Forsyt. From CRM connection to daily action prescriptions, your team will be live in hours, not months.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          {sections.map((section, i) => {
            const Icon = section.icon;
            return (
              <div key={i} data-testid={`docs-section-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                    <p className="text-sm text-gray-500">{section.desc}</p>
                  </div>
                </div>
                <div className="space-y-3 pl-2">
                  {section.steps.map((step, j) => (
                    <div key={j} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <span className="text-sm font-bold text-blue-600 bg-blue-50 rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        {j + 1}
                      </span>
                      <p className="text-gray-700 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to try it yourself?</h2>
          <p className="text-gray-600 mb-8">Explore the interactive sandbox with sample data, or book a call to see it with your pipeline.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-8 py-3.5 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all"
            >
              Try the Sandbox <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3.5 text-base font-semibold border-2 border-gray-300 hover:border-blue-600 bg-white rounded-full transition-all"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
